import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Copy, CheckCircle2, X, MapPin } from "lucide-react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ImageUploadField from "@/components/shared/ImageUploadField";
import { LocationSelector } from "@/components/shared/LocationSelector";
import { adminProvidersApi } from "@/lib/api/adminProviders";
import { categoriesApi } from "@/lib/api/categories";
import { providerTypesApi } from "@/lib/api/providerTypes";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

const KYC_STATUSES = ["DRAFT", "PENDING", "APPROVED", "REJECTED"];

const EMPTY = {
    // New required fields
    legal_name_en: "",
    legal_name_ar: "",
    display_name_en: "",
    display_name_ar: "",
    primary_phone: "",
    primary_email: "",
    // Existing fields
    corporate_name: "",
    type: "CLINIC",
    description_en: "",
    description_ar: "",
    logo_url: null,
    license_number: "",
    license_expiry: "",
    kyc_status: "PENDING",
    is_visible: false,
    category_ids: [],
    admin_full_name: "",
    admin_email: "",
    admin_password: "",
    initial_location: {
        name_en: "",
        name_ar: "",
        branch_name_en: "",
        branch_name_ar: "",
        governorate_en: "",
        governorate_ar: "",
        city_en: "",
        city_ar: "",
        neighborhood_en: "",
        neighborhood_ar: "",
        address_line_1_en: "",
        address_line_1_ar: "",
        address_line_2_en: "",
        address_line_2_ar: "",
        floor_suite: "",
        floor_suite_ar: "",
        phone: "",
        branch_phone: "",
        google_maps_url: "",
        hours_text: "",
        is_active: true,
        // Keep legacy fields for backward compatibility
        address_en: "",
        address_ar: "",
        city: "",
        neighborhood: "",
    },
};

export default function CreateProviderDialog({ open, onOpenChange, onCreated }) {
    const { t, isRTL, lang } = useI18n();
    const [form, setForm] = useState(EMPTY);
    const [tree, setTree] = useState([]);
    const [providerTypes, setProviderTypes] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [created, setCreated] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(EMPTY); setError(""); setCreated(null); setCopied(false);
            categoriesApi.listTree(false).then(setTree).catch(() => setTree([]));
            providerTypesApi.listPublic().then(setProviderTypes).catch(() => setProviderTypes([]));
        }
    }, [open]);

    const update = (patch) => setForm((p) => ({ ...p, ...patch }));

    // Flat L2 list with parent labels for nicer rendering.
    const allL2 = useMemo(() => {
        const out = [];
        const seen = new Set();
        (tree || []).forEach((l1) => {
            (l1.children || []).forEach((l2) => {
                if (!seen.has(l2.id)) {
                    seen.add(l2.id);
                    out.push({ ...l2, _parent_en: l1.name_en, _parent_ar: l1.name_ar });
                }
            });
        });
        return out;
    }, [tree]);

    const toggleCategory = (id) => {
        setForm((p) => {
            const has = p.category_ids.includes(id);
            return { ...p, category_ids: has ? p.category_ids.filter((c) => c !== id) : [...p.category_ids, id] };
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setSubmitting(true);
        try {
            const loc = form.initial_location;
            if (!loc.name_en.trim() || !loc.name_ar.trim()) {
                throw new Error(t("admin.providers.fields.locationRequired"));
            }
            if (!loc.governorate_en || !loc.neighborhood_en) {
                throw new Error("Please select governorate and neighborhood");
            }
            
            const payload = {
                // New required fields
                legal_name_en: form.legal_name_en.trim(),
                legal_name_ar: form.legal_name_ar.trim(),
                display_name_en: form.display_name_en.trim(),
                display_name_ar: form.display_name_ar.trim(),
                primary_phone: form.primary_phone.trim(),
                primary_email: form.primary_email.trim().toLowerCase(),
                // Existing fields
                corporate_name: form.corporate_name.trim(),
                type: form.type,
                description_en: form.description_en.trim() || null,
                description_ar: form.description_ar.trim() || null,
                logo_url: form.logo_url || null,
                license_number: form.license_number.trim() || null,
                license_expiry: form.license_expiry || null,
                kyc_status: form.kyc_status,
                is_visible: form.is_visible,
                category_ids: form.category_ids,
                initial_location: {
                    name_en: loc.name_en.trim(),
                    name_ar: loc.name_ar.trim(),
                    branch_name_en: loc.branch_name_en?.trim() || null,
                    branch_name_ar: loc.branch_name_ar?.trim() || null,
                    governorate_en: loc.governorate_en,
                    governorate_ar: loc.governorate_ar,
                    city_en: loc.city_en || loc.governorate_en,
                    city_ar: loc.city_ar || loc.governorate_ar,
                    neighborhood_en: loc.neighborhood_en,
                    neighborhood_ar: loc.neighborhood_ar,
                    address_line_1_en: loc.address_line_1_en?.trim() || null,
                    address_line_1_ar: loc.address_line_1_ar?.trim() || null,
                    address_line_2_en: loc.address_line_2_en?.trim() || null,
                    address_line_2_ar: loc.address_line_2_ar?.trim() || null,
                    floor_suite: loc.floor_suite?.trim() || null,
                    phone: loc.phone?.trim() || null,
                    branch_phone: loc.branch_phone?.trim() || loc.phone?.trim() || null,
                    google_maps_url: loc.google_maps_url?.trim() || null,
                    hours_text: loc.hours_text?.trim() || null,
                    is_active: !!loc.is_active,
                    // Legacy fields for backward compatibility
                    address_en: loc.address_line_1_en?.trim() || null,
                    address_ar: loc.address_line_1_ar?.trim() || null,
                    city: loc.city_en || loc.governorate_en || null,
                    neighborhood: loc.neighborhood_en || null,
                    country: "EG",
                },
            };
            const adminEmail = form.admin_email.trim().toLowerCase();
            if (adminEmail) {
                payload.admin_email = adminEmail;
                payload.admin_full_name = form.admin_full_name.trim() || null;
                if (form.admin_password.trim()) payload.admin_password = form.admin_password.trim();
            }
            const res = await adminProvidersApi.create(payload);
            setCreated(res);
            onCreated?.(res);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const copyTempPwd = async () => {
        if (!created?.admin_temporary_password) return;
        try {
            await navigator.clipboard.writeText(created.admin_temporary_password);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch (_) { /* ignore */ }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl rounded-2xl max-h-[92vh] overflow-y-auto"
                data-testid="create-provider-dialog"
                dir={isRTL ? "rtl" : "ltr"}
            >
                {!created ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>{t("admin.providers.createTitle")}</DialogTitle>
                            <DialogDescription>{t("admin.providers.createDescription")}</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-4 mt-2">
                            <div className="flex items-start gap-4">
                                <ImageUploadField
                                    value={form.logo_url}
                                    onChange={(v) => update({ logo_url: v })}
                                    shape="square"
                                    maxDim={600}
                                    testIdPrefix="create-provider-logo"
                                    label={t("admin.providers.fields.logoLabel")}
                                />
                                <div className="flex-1 space-y-3">
                                    <div>
                                        <Label htmlFor="p_name">{t("admin.providers.fields.corporateName")}</Label>
                                        <Input
                                            id="p_name" required
                                            value={form.corporate_name}
                                            onChange={(e) => update({ corporate_name: e.target.value })}
                                            className="mt-1.5"
                                            data-testid="create-provider-name"
                                        />
                                    </div>
                                    <p className="text-[11px] text-muted-foreground">{t("admin.providers.fields.logoHint")}</p>
                                </div>
                            </div>

                            {/* Legal Name (Bilingual) */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label>{t("admin.providers.fields.legalNameEn")} <span className="text-red-500">*</span></Label>
                                    <Input
                                        dir="ltr" required
                                        value={form.legal_name_en}
                                        onChange={(e) => update({ legal_name_en: e.target.value })}
                                        className="mt-1.5"
                                        placeholder="e.g., Cairo Medical Center LLC"
                                    />
                                </div>
                                <div>
                                    <Label>{t("admin.providers.fields.legalNameAr")} <span className="text-red-500">*</span></Label>
                                    <Input
                                        dir="rtl" lang="ar" required
                                        value={form.legal_name_ar}
                                        onChange={(e) => update({ legal_name_ar: e.target.value })}
                                        className="mt-1.5 font-arabic"
                                        placeholder="مثال: مركز القاهرة الطبي ذ.م.م"
                                    />
                                </div>
                            </div>

                            {/* Display Name (Bilingual) */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label>{t("admin.providers.fields.displayNameEn")} <span className="text-red-500">*</span></Label>
                                    <Input
                                        dir="ltr" required
                                        value={form.display_name_en}
                                        onChange={(e) => update({ display_name_en: e.target.value })}
                                        className="mt-1.5"
                                        placeholder="e.g., Cairo Medical"
                                    />
                                </div>
                                <div>
                                    <Label>{t("admin.providers.fields.displayNameAr")} <span className="text-red-500">*</span></Label>
                                    <Input
                                        dir="rtl" lang="ar" required
                                        value={form.display_name_ar}
                                        onChange={(e) => update({ display_name_ar: e.target.value })}
                                        className="mt-1.5 font-arabic"
                                        placeholder="مثال: القاهرة الطبي"
                                    />
                                </div>
                            </div>

                            {/* Primary Contact */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label>{t("admin.providers.fields.primaryPhone")} <span className="text-red-500">*</span></Label>
                                    <Input
                                        dir="ltr" type="tel" required
                                        value={form.primary_phone}
                                        onChange={(e) => update({ primary_phone: e.target.value })}
                                        className="mt-1.5"
                                        placeholder="+20 123 456 7890"
                                    />
                                </div>
                                <div>
                                    <Label>{t("admin.providers.fields.primaryEmail")} <span className="text-red-500">*</span></Label>
                                    <Input
                                        dir="ltr" type="email" required
                                        value={form.primary_email}
                                        onChange={(e) => update({ primary_email: e.target.value })}
                                        className="mt-1.5"
                                        placeholder="contact@provider.com"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label>{t("admin.providers.fields.type")}</Label>
                                    <Select value={form.type} onValueChange={(v) => update({ type: v })}>
                                        <SelectTrigger className="mt-1.5" data-testid="create-provider-type">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {providerTypes.map((typ) => (
                                                <SelectItem key={typ.code} value={typ.code}>
                                                    {lang === "ar" ? typ.name_ar : typ.name_en}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>{t("admin.providers.fields.kycStatus")}</Label>
                                    <Select value={form.kyc_status} onValueChange={(v) => update({ kyc_status: v })}>
                                        <SelectTrigger className="mt-1.5" data-testid="create-provider-kyc">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {KYC_STATUSES.map((s) => (
                                                <SelectItem key={s} value={s}>{t(`admin.providers.kyc.${s}`)}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="p_desc_en">{t("admin.providers.fields.descriptionEn")}</Label>
                                    <Textarea
                                        id="p_desc_en" dir="ltr" rows={3}
                                        value={form.description_en}
                                        onChange={(e) => update({ description_en: e.target.value })}
                                        className="mt-1.5"
                                        data-testid="create-provider-desc-en"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="p_desc_ar">{t("admin.providers.fields.descriptionAr")}</Label>
                                    <Textarea
                                        id="p_desc_ar" dir="rtl" rows={3}
                                        value={form.description_ar}
                                        onChange={(e) => update({ description_ar: e.target.value })}
                                        className="mt-1.5"
                                        data-testid="create-provider-desc-ar"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="p_lic">{t("admin.providers.fields.licenseNumber")}</Label>
                                    <Input
                                        id="p_lic" dir="ltr"
                                        value={form.license_number}
                                        onChange={(e) => update({ license_number: e.target.value })}
                                        className="mt-1.5"
                                        data-testid="create-provider-license"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="p_exp">{t("admin.providers.fields.licenseExpiry")}</Label>
                                    <Input
                                        id="p_exp" type="date"
                                        value={form.license_expiry}
                                        onChange={(e) => update({ license_expiry: e.target.value })}
                                        className="mt-1.5"
                                        data-testid="create-provider-license-expiry"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label>{t("admin.providers.fields.categories")}</Label>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {t("admin.providers.fields.categoriesHint")}
                                </p>
                                <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-border p-2 space-y-1">
                                    {allL2.length === 0 && (
                                        <p className="text-xs text-muted-foreground p-2">
                                            {t("admin.providers.fields.noCategories")}
                                        </p>
                                    )}
                                    {allL2.map((c) => {
                                        const checked = form.category_ids.includes(c.id);
                                        return (
                                            <label
                                                key={c.id}
                                                className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer text-sm ${checked ? "bg-secondary" : "hover:bg-muted/40"}`}
                                                data-testid={`create-provider-cat-${c.id}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleCategory(c.id)}
                                                    className="h-4 w-4 accent-primary"
                                                />
                                                <span className="font-medium">
                                                    {lang === "ar" ? (c.name_ar || c.name_en) : c.name_en}
                                                </span>
                                                <span className="ms-auto text-xs text-muted-foreground">
                                                    {lang === "ar" ? c._parent_ar : c._parent_en}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                                {form.category_ids.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {form.category_ids.map((id) => {
                                            const c = allL2.find((x) => x.id === id);
                                            if (!c) return null;
                                            return (
                                                <span
                                                    key={id}
                                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs"
                                                >
                                                    {lang === "ar" ? (c.name_ar || c.name_en) : c.name_en}
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleCategory(id)}
                                                        className="hover:bg-primary/20 rounded-full p-0.5"
                                                        aria-label="remove"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-between rounded-xl border border-border p-3">
                                <div>
                                    <Label className="cursor-pointer">{t("admin.providers.fields.isVisible")}</Label>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {t("admin.providers.fields.isVisibleHint")}
                                    </p>
                                </div>
                                <Switch
                                    checked={form.is_visible}
                                    onCheckedChange={(v) => update({ is_visible: v })}
                                    data-testid="create-provider-visible"
                                />
                            </div>

                            <div className="rounded-xl border border-border bg-muted/20 p-3 space-y-3">
                                <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-primary" />
                                    <p className="text-sm font-semibold">{t("admin.providers.fields.defaultLocation")}</p>
                                </div>
                                <p className="text-xs text-muted-foreground">{t("admin.providers.fields.defaultLocationHint")}</p>
                                
                                {/* Branch Name Fields */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <Label>{t("provider.locations.fields.nameEn")}</Label>
                                        <Input
                                            dir="ltr" required
                                            value={form.initial_location.name_en}
                                            onChange={(e) => update({ initial_location: { ...form.initial_location, name_en: e.target.value } })}
                                            className="mt-1.5"
                                            placeholder="e.g., Main Branch"
                                            data-testid="create-provider-loc-name-en"
                                        />
                                    </div>
                                    <div>
                                        <Label>{t("provider.locations.fields.nameAr")}</Label>
                                        <Input
                                            dir="rtl" lang="ar" required
                                            value={form.initial_location.name_ar}
                                            onChange={(e) => update({ initial_location: { ...form.initial_location, name_ar: e.target.value } })}
                                            className="mt-1.5 font-arabic"
                                            placeholder="مثال: الفرع الرئيسي"
                                            data-testid="create-provider-loc-name-ar"
                                        />
                                    </div>
                                </div>

                                {/* Location Selector Component */}
                                <LocationSelector
                                    value={form.initial_location}
                                    onChange={(locData) => update({ initial_location: { ...form.initial_location, ...locData } })}
                                    required={true}
                                />

                                {/* Phone Number */}
                                <div>
                                    <Label>{t("provider.locations.fields.phone")}</Label>
                                    <Input
                                        dir="ltr"
                                        value={form.initial_location.phone}
                                        onChange={(e) => update({ initial_location: { ...form.initial_location, phone: e.target.value } })}
                                        className="mt-1.5"
                                        placeholder="+20 123 456 7890"
                                        data-testid="create-provider-loc-phone"
                                    />
                                </div>
                            </div>

                            <div className="rounded-xl border border-border bg-muted/20 p-3 space-y-3">
                                <div>
                                    <p className="text-sm font-semibold">{t("admin.providers.adminSection")}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {t("admin.providers.adminSectionHint")}
                                    </p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <Label htmlFor="p_admin_email">{t("admin.providers.fields.adminEmail")}</Label>
                                        <Input
                                            id="p_admin_email" type="email" dir="ltr"
                                            value={form.admin_email}
                                            onChange={(e) => update({ admin_email: e.target.value })}
                                            className="mt-1.5"
                                            data-testid="create-provider-admin-email"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="p_admin_name">{t("admin.providers.fields.adminFullName")}</Label>
                                        <Input
                                            id="p_admin_name"
                                            value={form.admin_full_name}
                                            onChange={(e) => update({ admin_full_name: e.target.value })}
                                            className="mt-1.5"
                                            data-testid="create-provider-admin-name"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="p_admin_pwd">{t("admin.providers.fields.adminPassword")}</Label>
                                    <Input
                                        id="p_admin_pwd" type="text" dir="ltr"
                                        placeholder={t("admin.providers.fields.adminPasswordPlaceholder")}
                                        value={form.admin_password}
                                        onChange={(e) => update({ admin_password: e.target.value })}
                                        className="mt-1.5"
                                        data-testid="create-provider-admin-password"
                                    />
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        {t("admin.providers.fields.adminPasswordHint")}
                                    </p>
                                </div>
                            </div>

                            {error && (
                                <div
                                    className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                                    data-testid="create-provider-error"
                                >
                                    {error}
                                </div>
                            )}

                            <DialogFooter>
                                <Button
                                    type="button" variant="outline"
                                    onClick={() => onOpenChange(false)}
                                    data-testid="create-provider-cancel"
                                >
                                    {t("common.cancel")}
                                </Button>
                                <Button type="submit" disabled={submitting} data-testid="create-provider-submit">
                                    {submitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                                    {t("admin.providers.createSubmit")}
                                </Button>
                            </DialogFooter>
                        </form>
                    </>
                ) : (
                    <div data-testid="create-provider-success">
                        <DialogHeader>
                            <div className="flex items-center gap-2">
                                <div className="icon-tile h-10 w-10">
                                    <CheckCircle2 className="h-5 w-5" />
                                </div>
                                <div>
                                    <DialogTitle>{t("admin.providers.successTitle")}</DialogTitle>
                                    <DialogDescription>{t("admin.providers.successDescription")}</DialogDescription>
                                </div>
                            </div>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                            <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
                                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                    {t("admin.providers.fields.corporateName")}
                                </p>
                                <p className="font-semibold mt-0.5" data-testid="success-provider-name">
                                    {created.corporate_name}
                                </p>
                            </div>

                            {created.admin_temporary_password && (
                                <div className="rounded-xl border border-primary/30 bg-secondary px-4 py-3">
                                    <p className="text-[11px] uppercase tracking-wide text-primary font-semibold">
                                        {t("admin.providers.tempPasswordLabel")}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {created.admin?.email}
                                    </p>
                                    <div className="mt-1.5 flex items-center gap-2">
                                        <code
                                            className="flex-1 font-mono text-base text-foreground break-all"
                                            data-testid="success-provider-temp-password"
                                        >
                                            {created.admin_temporary_password}
                                        </code>
                                        <Button
                                            type="button"
                                            size="icon"
                                            variant="outline"
                                            onClick={copyTempPwd}
                                            data-testid="success-provider-copy-password"
                                            aria-label="Copy password"
                                        >
                                            {copied ? <CheckCircle2 className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                                        </Button>
                                    </div>
                                    <p className="mt-2 text-xs text-muted-foreground">
                                        {t("admin.providers.tempPasswordHint")}
                                    </p>
                                </div>
                            )}
                        </div>
                        <DialogFooter className="mt-6">
                            <Button onClick={() => onOpenChange(false)} data-testid="create-provider-done">
                                {t("admin.members.done")}
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
